// proposal-existing-ticket-guards.ts
//
// Luật cho một `proposed_change` nhắm vào ticket ĐÃ CÓ (cancel/amend/
// supersede). Tách khỏi proposal-applier.ts (257 dòng, quá ngân sách 200) —
// applier giữ phần giao dịch, file này giữ phần "worker được đổi cái gì".
//
// Trả về lý do từ chối, hoặc `undefined` khi thay đổi chấp nhận được.

import { BOOTSTRAP_KINDS, type ProposedChange } from "./proposal-change-types.js";
import { checkProposedScope } from "./proposal-scope-guard.js";
import type { Ticket } from "./schemas/ticket-schema.js";

export function checkExistingTicketChange(
  target: Ticket,
  change: ProposedChange,
): string | undefined {
  const ticketId = target.id;
  // Bug L (P16): G2's enforced conditions are computed FROM ticket state —
  // the effective ticket count, and whether any ticket is `kind: "deploy"`.
  // A worker-authored proposal could therefore cancel the plan's only deploy
  // ticket, or relabel it, and watch `decideG2Requirement` flip from
  // "required" to "waivable" on exactly the plan G2 exists to catch. Same
  // shape as Bug K, one level up: not a forged field, but the governed party
  // editing the inputs to its own gate decision.
  //
  // Destructive work is therefore immutable through this path in BOTH
  // directions: a deploy ticket cannot be cancelled/superseded/relabelled
  // away, and an ordinary ticket cannot be promoted INTO one after a gate
  // decision was already taken. Changing destructive scope needs a human —
  // a `decision` ticket plus a gate approval, not an automatic proposal from
  // an unrelated worker's attempt.
  // `applyProposal` (proposal-applier.ts) already returns early for "create",
  // so every op reaching here targets an EXISTING ticket — cancel, amend or
  // supersede.
  const DEPLOY_KIND = "deploy";
  if (target.kind === DEPLOY_KIND) {
    return (
      `proposal không được ${change.op} ticket ${ticketId} vì nó là kind "${DEPLOY_KIND}" ` +
      "(việc destructive/migration mà G2 đang canh) — đổi phạm vi này cần người duyệt."
    );
  }
  // `create` is not the only route to this field: `amend` reaches it one
  // call later. Closing only `create` leaves the chain intact.
  if (change.op === "amend") {
    const amendScopeReason = checkProposedScope(change.patch?.allowed_paths);
    if (amendScopeReason !== undefined) return amendScopeReason;
  }
  if (change.op === "amend" && change.patch?.kind === DEPLOY_KIND) {
    return (
      `proposal không được đổi ticket ${ticketId} thành kind "${DEPLOY_KIND}" — ` +
      "thêm việc destructive sau khi gate đã quyết cần người duyệt."
    );
  }
  // Audit 2026-09-14 finding 1, second layer. `toApplierChange` already refuses
  // this at the wire boundary; refusing it again here mirrors how DEPLOY_KIND is
  // defended in both places, so a future caller reaching this transaction by
  // another route cannot promote a ticket into a gate-exempt kind.
  if (change.op === "amend" && BOOTSTRAP_KINDS.includes(change.patch?.kind as string)) {
    return (
      `proposal không được đổi ticket ${ticketId} thành kind "${change.patch?.kind}" ` +
      "(bootstrap kind) — survey/plan được miễn gate G0/G1 nên đổi sang chúng là tự né gate."
    );
  }

  return undefined;
}
